import {
  cacheKeys,
  FeatureName,
  hasAssetAccess,
  isGuestWorkspaceRole,
  PermissionValues,
  useCurrentAccount,
  useIsFeatureEnabled,
  useRoles,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AssetHeader,
  Button,
  ChevronRightIcon,
  Colors,
  Icon,
  LoadingIndicator,
  SlideAction,
  type SlideLayoutLocationState,
  getAutoPromptProps,
  tokens,
  useResponsiveDesign,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCall } from '../../calls/CallProvider';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { useAssetRouting } from '../assetRouteContext';
import {
  RoutineSubmitSource,
  isActivityOrEditPage,
  shouldUseAssetSubrouteInitialTransition,
} from '../assetUtils';
import { AssetActivityFold } from './assetActivityFold';
import { AssetMediaFold } from './assetMediaFold';
import { AssetRoutineFold } from './assetRoutineFold';
import { RequestAccessModal } from './requestAccessModal';

export const AssetCard = ({
  assetId,
  forceShowing,
}: {
  assetId: string;
  forceShowing?: boolean;
}) => {
  return <AssetCardBase assetId={assetId} forceShowing={forceShowing} />;
};

export const AssetCardBase = ({
  assetId,
  forceShowing,
}: {
  assetId: string;
  forceShowing?: boolean;
}) => {
  const { onSlideChange, prepareSlideTransition, setColumnTitle } =
    useSlideLayout();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition = locationState?.slideLayoutInitialTransition;
  const isSlideTransitionMount =
    initialTransition === SlideAction.NEXT ||
    initialTransition === SlideAction.PREV;
  // Read the context issue ID from the URL (present when inside an activities route).
  const { issueId: contextIssueId } = useParams();
  const { basePath, isWorkspaceMode } = useAssetRouting(assetId);
  const [selectedIssueId, setSelectedIssueId] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const {
    workspaceAsset,
    isLoading: isLoadingWorkspaceAsset,
    invalidateQuery: invalidateWorkspaceAsset,
  } = useWorkspaceAsset(assetId, !assetId);
  const navigate = useNavigate();
  const isResponsiveDesign = useResponsiveDesign();
  const queryClient = useQueryClient();
  const isEditPage = isActivityOrEditPage(location);
  const isWeb = Platform.OS === 'web';

  // "Call Dealer" — gated identically to the backend ringActivityCall resolver
  // (COMMUNICATION_PLATFORM + call.create) so the UI never offers a call that
  // would FORBIDDEN on submit. `startAssetCall` is only present when canCall.
  const { account: currentAccount, isLoading: isAccountLoading } =
    useCurrentAccount();
  const call = useCall();
  const isCommunicationPlatformEnabled = useIsFeatureEnabled(
    FeatureName.CommunicationPlatform
  );
  const dealerCallAssetName = workspaceAsset?.asset?.name;
  const dealerCallWorkspaceId = workspaceAsset?.workspace?.id;

  // ALLOWLIST, not deny-list (plan 0030 Phase 5): a guest gets exactly the
  // header and their own conversation. Everything else in this component —
  // routines, media, "more asset info", Call Dealer (Guest holds
  // call.create, so it renders unless excluded here) — lives inside the
  // `!isGuestOfAssetWorkspace` guard below so a section added later is off
  // for a guest by default, not on until someone remembers to hide it.
  // Fails closed: unresolved account/roles/workspace data reads as guest.
  const { roles, isLoading: isRolesLoading } = useRoles();
  const isGuestOfAssetWorkspace =
    isAccountLoading || isRolesLoading || !dealerCallWorkspaceId
      ? true
      : isGuestWorkspaceRole(
          dealerCallWorkspaceId,
          currentAccount?.workspaceRoles,
          roles
        );

  // Lonely larry (signed in, no membership in the asset's workspace): the same
  // rule the backend applies to asset reads, resolved fail-closed — an
  // unresolved account or asset reads as "no access", which hides the member
  // sections but, via `accessResolved`, does not yet offer the request button
  // either. Members and Guests are unaffected: a member has access; a Guest is
  // handled by the allowlist above and never reaches this branch.
  const accessResolved = !isAccountLoading && !!workspaceAsset;
  const hasAccess = hasAssetAccess(currentAccount, workspaceAsset);
  const isLonelyLarry = !isGuestOfAssetWorkspace && !hasAccess;

  // An access request is approved in someone ELSE's session, and the account
  // query is cached for hours, so the requester's own page would keep showing
  // the Guest / request-access state until something unrelated refetched it.
  // Re-read the account once per asset whenever the cached one says "not a
  // full member" — the one state a role change elsewhere can invalidate.
  // Members skip this; the workspaceAsset is already refreshed on open above.
  const refreshedAccountFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!accessResolved || !assetId) return;
    if (!isGuestOfAssetWorkspace && hasAccess) return;
    if (refreshedAccountFor.current === assetId) return;
    refreshedAccountFor.current = assetId;
    // v5 invalidateQueries resolves even when the refetch fails (throwOnError
    // is opt-in), so this only guards an internal rejection — release the
    // once-per-asset latch so a later render can try again.
    queryClient.invalidateQueries({ queryKey: ['currentAccount'] }).catch(() => {
      refreshedAccountFor.current = undefined;
    });
  }, [accessResolved, assetId, hasAccess, isGuestOfAssetWorkspace, queryClient]);
  const [accessRequestVisible, setAccessRequestVisible] = useState(false);
  const [accessRequested, setAccessRequested] = useState(false);

  const canCallDealer =
    !isGuestOfAssetWorkspace &&
    hasAccess &&
    !isEditPage &&
    isCommunicationPlatformEnabled &&
    currentAccount?.hasPermission(PermissionValues.call.create) &&
    !!call?.startAssetCall &&
    !!dealerCallAssetName &&
    !!dealerCallWorkspaceId;
  const onCallDealer = () => {
    if (!dealerCallAssetName || !dealerCallWorkspaceId) {
      return;
    }
    call?.startAssetCall?.({
      assetId,
      assetName: dealerCallAssetName,
      workspaceId: dealerCallWorkspaceId,
    });
  };

  // Reset component state when assetId changes
  useEffect(() => {
    if (isSlideTransitionMount) return;

    invalidateWorkspaceAsset();
  }, [assetId, invalidateWorkspaceAsset, isSlideTransitionMount]);

  // Auto-prompt routines on asset open for mobile only
  useEffect(() => {
    if (isWeb || isLoadingWorkspaceAsset || !workspaceAsset) {
      return;
    }

    if (workspaceAsset.asset?.id !== assetId) {
      return;
    }

    if (!isEditPage) {
      const autoPrompt = getAutoPromptProps(workspaceAsset);

      if (autoPrompt) {
        navigate(
          `/assets/${assetId}/routines/${autoPrompt.first.routine?.id}`,
          { state: { from: RoutineSubmitSource.AssetCard } }
        );
      }
    }
  }, [isLoadingWorkspaceAsset, workspaceAsset, assetId, isEditPage]);

  useEffect(() => {
    if (workspaceAsset) {
      setColumnTitle(workspaceAsset.asset?.name || 'assets.menu.title');
    }
  }, [workspaceAsset, setColumnTitle]);

  useEffect(() => {
    return () => {
      setColumnTitle(undefined);
    };
  }, [setColumnTitle]);

  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);

  const onIssueSelected = async (issueId: string) => {
    setSelectedIssueId(issueId);
    const nextPath = `${basePath}/activity/${issueId}`;

    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(nextPath, {
        state: {
          from: RoutineSubmitSource.AssetCard,
          slideLayoutInitialTransition: SlideAction.NEXT,
        },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    // In activities context use the route's contextIssueId (the activity the
    // user was viewing when they opened the asset panel) as the first segment.
    // Using the selected issueId for both positions would lose the original context.
    navigate(nextPath, {
      state: { from: RoutineSubmitSource.AssetCard },
    });
  };

  const onAssetEdit = async () => {
    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(`${basePath}/edit`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    startTransition(() => navigate(`${basePath}/edit`));
  };

  const onHeroImagePress = async () => {
    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(`${basePath}/image`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    startTransition(() => navigate(`${basePath}/image`));
  };

  // Off the allowlist for a guest — see isGuestOfAssetWorkspace above — and
  // off for a lonely larry, whose edit page would be a FORBIDDEN dead-end.
  const viewAssetInfoLink =
    !isGuestOfAssetWorkspace &&
    hasAccess &&
    !location.pathname.includes('edit') ? (
      <Pressable style={styles.viewAssetButton} onPress={onAssetEdit}>
        <Text style={styles.viewAssetButtonLabel}>
          {`${t(
            isWorkspaceMode
              ? 'topNav.moreWorkspaceInfo'
              : 'topNav.moreAssetInfo'
          )}`}
        </Text>
        <ChevronRightIcon size={20} color={Colors.role.textPrimary} />
      </Pressable>
    ) : null;

  if (!assetId) {
    return null;
  }

  if (isLoadingWorkspaceAsset || !workspaceAsset) {
    return <LoadingIndicator flexContainer />;
  }

  // Hero-image edit affordances are withheld for a guest (allowlist) and for a
  // lonely larry (the update would FORBIDDEN).
  const canEditHeroImage = !isGuestOfAssetWorkspace && hasAccess;

  return (
    workspaceAsset !== undefined && (
      <View style={styles.container}>
        <View style={styles.containerHeader}>
          <AssetHeader
            workspaceAsset={workspaceAsset!}
            additionalHeaderBlock={viewAssetInfoLink}
            assetDetailsContainerStyle={styles.assetDetailsContainerStyle}
            imgSize={isResponsiveDesign ? 85 : 112}
            // Off the allowlist for a guest — see isGuestOfAssetWorkspace
            // above. AssetHeader's own prop hides the location/routine-state
            // subheader; the hero-image edit affordance is withheld here.
            invalidateQuery={
              canEditHeroImage
                ? async () => await invalidateWorkspaceAsset()
                : undefined
            }
            mediaPicker={canEditHeroImage ? MediaPicker : undefined}
            onImagePress={canEditHeroImage ? onHeroImagePress : undefined}
            isGuestOfAssetWorkspace={isGuestOfAssetWorkspace}
          />
        </View>
        {!isGuestOfAssetWorkspace && hasAccess && (
          <AssetRoutineFold assetId={assetId} forceShowing={forceShowing} />
        )}
        {/* A lonely larry's activity list would be filtered by their own
            personal workspace — the conversation they came from is the
            column to the left, so the fold is withheld rather than empty. */}
        {!isLonelyLarry && (
          <AssetActivityFold
            assetId={assetId}
            onIssueSelected={onIssueSelected}
            selectedIssue={selectedIssueId}
            showAssetName={false}
            forceShowing={forceShowing}
          />
        )}
        {!isGuestOfAssetWorkspace && (
          <AssetMediaFold
            workspaceAsset={workspaceAsset}
            assetId={assetId}
            forceShowing={forceShowing}
            readOnly={!hasAccess}
          />
        )}
        {/* Request access is ON the guest allowlist: without it a Guest's
            only route to more access is an admin promoting them unprompted. */}
        {(isLonelyLarry || isGuestOfAssetWorkspace) && accessResolved && (
          <>
            <View style={styles.fullWidthActionContainer}>
              <Button
                variant="rectangular"
                state={accessRequested ? 'default' : 'active'}
                size="large"
                disabled={accessRequested}
                onPress={() => setAccessRequestVisible(true)}
                style={styles.fullWidthActionButton}
                testID="asset-card-request-access-button"
                accessibilityLabel={t(
                  accessRequested
                    ? 'asset.accessRequest.requested'
                    : 'asset.accessRequest.requestFull'
                )}
              >
                {t(
                  accessRequested
                    ? 'asset.accessRequest.requested'
                    : 'asset.accessRequest.requestFull'
                )}
              </Button>
            </View>
            <RequestAccessModal
              visible={accessRequestVisible}
              assetId={assetId}
              workspaceId={workspaceAsset.workspace.id}
              onClose={() => setAccessRequestVisible(false)}
              onSuccess={() => setAccessRequested(true)}
            />
          </>
        )}
        {canCallDealer && (
          <View style={styles.fullWidthActionContainer}>
            <Button
              variant="rectangular"
              state="default"
              size="large"
              onPress={onCallDealer}
              style={styles.fullWidthActionButton}
              testID="asset-card-call-button"
              accessibilityLabel="Call Dealer"
              leftIcon={
                <Icon
                  name="PhoneCall"
                  size={18}
                  color={Colors.role.textStrong}
                />
              }
            >
              {t('assets.callDealer', 'Call Dealer')}
            </Button>
          </View>
        )}
      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerHeader: {
    paddingTop: 6,
    paddingHorizontal: tokens.spacing.lg,
    backgroundColor: Colors.role.canvas,
  },
  assetDetailsContainerStyle: {
    height: 112,
    justifyContent: 'space-between',
  },
  viewAssetButton: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 24,
    marginTop: 5,
    paddingVertical: 0,
    marginVertical: 0,
  },
  viewAssetButtonLabel: {
    ...tokens.typography.label.medium,
    fontWeight: tokens.typography.label.mediumEmphasised.fontWeight,
    color: Colors.role.textPrimary,
    marginRight: 0,
    padding: 0,
  },
  fullWidthActionContainer: {
    marginTop: 'auto',
    marginHorizontal: tokens.spacing.lg,
    marginBottom: tokens.spacing.lg,
  },
  fullWidthActionButton: {
    alignSelf: 'stretch',
    borderRadius: tokens.borderRadius.md,
  },
});
